import {
  OrgFrozenBillingNotice,
  OrgFrozenScreen,
  OrgTrialEndedBanner,
} from '@/components/features/organisations/OrgFrozenScreen'
import { OrgPendingDeletionScreen } from '@/components/features/organisations/OrgPendingDeletionScreen'
import NotFound from '@/components/utils/NotFound'
import OutsideSupportSession from '@/components/utils/OutsideSupportSession'
import PermissionGate from '@/components/utils/PermissionGate'
import RouteLoadFailed from '@/components/utils/RouteLoadFailed'
import { useClient } from '@/context/ClientContext'
import { refusalKindOf } from '@/context/SupportSessionContext'
import { usePermissionCheck } from '@/hooks/usePermissionCheck'
import { fetchProjects } from '@/lib/clientHelpers'
import { classifyLoadFailure, shouldForgetLastGood } from '@/lib/loader-errors'
import { cn, formatBytes } from '@/lib/utils'
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import { UseQueryResult, useQuery } from '@tanstack/react-query'
import {
  Link,
  Outlet,
  createFileRoute,
  notFound,
  useLocation,
} from '@tanstack/react-router'
import { useState } from 'react'

import {
  Tabs,
  TabsContent,
  TabsCount,
  TabsList,
  TabsTrigger,
} from '@droidmap/frontend-ui'
import { Spinner } from '@droidmap/frontend-ui'
import {
  Organisation,
  isExpiredCreditTrial,
} from '@droidmap/organisation-service-contract'
import { Role } from '@droidmap/permissions-service-contract'
import { Project } from '@droidmap/project-service-contract'
import {
  DroidMapMetadata,
  StorageSummary,
} from '@droidmap/shared-service-contract'

/**
 * The org this route last loaded successfully, and for whom (#2658).
 *
 * Exists so a failed *revalidation* can keep showing what the user is already
 * looking at instead of tearing the page down. That matters most for the
 * settlement poll (#2655), which revalidates this loader every 10s for up to
 * three minutes while a plan's fee settles: one 5xx on one tick used to replace
 * the page with "Organization not found" — and unmount the poll that would have
 * recovered.
 *
 * A single slot, not a map: it cannot grow, and only one org is ever on
 * screen. Both halves of the key are load-bearing, and neither is obvious:
 *
 *  - **orgId**, because the router derives `cause` from whether it saw a
 *    previous match for this **routeId** (`previousMatchesByRouteId`), not
 *    this match — so navigating org A → org B is a `'stay'` too. Without the
 *    org in the key, a 5xx on that navigation would seat the user on org A's
 *    name and storage figures under org B's URL.
 *  - **userId**, so a logout and log back in as someone else in the same tab
 *    can never be served the previous user's org.
 */
let lastGoodOrg: { key: string; org: Organisation } | undefined

const lastGoodOrgKey = (orgId: string, userId: string) => `${userId}/${orgId}`

export const Route = createFileRoute('/_authenticated/orgs/$orgId')({
  beforeLoad: async ({ params, context }) => {
    const { orgId } = params
    const metadata: DroidMapMetadata = {
      ...context.metadata,
      orgId,
    }
    return { metadata }
  },
  loader: async ({ params, context, cause }) => {
    const { orgId } = params
    const { organisationClient } = context.client
    const { metadata } = context
    const key = lastGoodOrgKey(orgId, metadata.userId)
    try {
      const org = await organisationClient.getOrganisation({ orgId }, metadata)
      lastGoodOrg = { key, org }
      return { crumb: org.name, orgId, org, metadata }
    } catch (error) {
      // A support session refused the org (one outside the org it covers):
      // its own page, never "not found", which is what the 403 would become.
      if (refusalKindOf(error)) {
        throw error
      }
      const previous = lastGoodOrg?.key === key ? lastGoodOrg.org : undefined
      // Only a genuine 404/403 is "not found" — a 5xx, a network blip or a
      // token refresh landing mid-flight is a claim about us, not about the
      // user's data (#2658). See `classifyLoadFailure` for the full matrix.
      const outcome = classifyLoadFailure(error, {
        cause,
        hasLastGood: previous !== undefined,
      })

      // Key-matched so a failed load of org B can't wipe org A's fallback
      // while the user is sitting on A mid-poll. See `shouldForgetLastGood`
      // for why an `'error'` must drop it: `invalidate()` leaves the errored
      // match in `state.matches`, so Try again comes back as a `'stay'` and
      // would otherwise be served this snapshot as if the call had worked.
      if (shouldForgetLastGood(outcome, cause) && lastGoodOrg?.key === key) {
        lastGoodOrg = undefined
      }

      if (outcome === 'not-found') {
        console.error('Organisation not found', error)
        throw notFound()
      }

      if (outcome === 'stale' && previous) {
        // Deliberately invisible: a blip on one poll tick should cost the
        // user nothing. This holds for as long as they stay on this match,
        // even across repeated failures — the alternative is an error page
        // mid-poll that the settlement watch's own expiry copy already
        // covers ("this is taking longer than usual"). Navigating here
        // afresh is an `enter`, which always surfaces the real failure.
        console.warn(
          'Failed to reload organisation — keeping the last good copy',
          error,
        )
        return { crumb: previous.name, orgId, org: previous, metadata }
      }

      throw error
    }
  },
  component: OrgDashboardLayout,
  notFoundComponent: () => <NotFound resource="Organization" />,
  errorComponent: ({ error }) => <OrgLoadError error={error} />,
})

/** The org's failed load: a support session's refusal, or a failure of ours. */
export const OrgLoadError = ({ error }: { error: unknown }) => {
  const refused = refusalKindOf(error)
  return refused ? (
    <OutsideSupportSession kind={refused} />
  ) : (
    <RouteLoadFailed resource="organisation" />
  )
}

const totalStorageBytes = (s: StorageSummary | undefined): number => {
  if (!s) return 0
  return (Object.values(s) as unknown[]).reduce<number>(
    (sum, v) => sum + (typeof v === 'number' ? v : 0),
    0,
  )
}

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const formatStorageValue = (bytes: number): { num: string; unit: string } => {
  const formatted = formatBytes(bytes, bytes >= 1024 * 1024 * 1024 ? 1 : 0)
  const [num, unit] = formatted.split(' ')
  return { num, unit: unit ?? 'B' }
}

interface IdCopyProps {
  id: string
}

function IdCopy({ id }: IdCopyProps) {
  const [copied, setCopied] = useState(false)
  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(id)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access can fail in non-secure contexts; silently ignore.
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title="Copy ID"
      className={cn(
        'bg-bg-2 border-border text-text-dim inline-flex items-center gap-2 rounded-xs border px-2.5 py-1 font-mono text-[11px] transition-colors duration-150',
        'hover:text-violet hover:border-violet/50',
      )}
    >
      {copied ? (
        <CheckIcon className="h-3 w-3" />
      ) : (
        <ClipboardDocumentIcon className="h-3 w-3" />
      )}
      <span className="truncate max-w-[18rem]">{id}</span>
    </button>
  )
}

interface OrgHeroProps {
  org: Organisation
  projectCount: number | undefined
}

function OrgHero({ org, projectCount }: OrgHeroProps) {
  const storageUsed = formatStorageValue(totalStorageBytes(org.storageSummary))

  return (
    <section
      className={cn(
        'grid-overlay halo-overlay relative mb-6 overflow-hidden rounded-lg border border-border bg-panel px-8 pb-6 pt-7',
      )}
    >
      <span
        aria-hidden
        className="bg-violet absolute inset-x-0 top-0 z-20 h-[3px]"
      />
      <div className="relative z-10 flex flex-wrap items-start justify-between gap-8">
        <div className="flex min-w-[320px] flex-1 items-start gap-[18px]">
          <div
            className={cn(
              'grid h-[60px] w-[60px] place-items-center rounded-md font-mono text-[20px] font-bold tracking-tight shadow-elevated',
              'bg-gradient-to-br from-[rgb(var(--violet))] to-[#7a6aff] text-white',
            )}
          >
            {initials(org.name)}
          </div>
          <div>
            <div className="text-micro text-violet mb-2.5 inline-flex items-center gap-2 rounded-full border border-violet/25 bg-violet-soft px-2.5 py-1">
              <span
                aria-hidden
                className="bg-violet inline-block h-1.5 w-1.5 rounded-full shadow-[0_0_10px_currentColor] animate-pulse"
              />
              Organisation
            </div>
            <h1 className="text-text mb-2 text-[28px] font-bold leading-tight tracking-tight">
              {org.name}
            </h1>
            <div className="text-text-dim flex flex-wrap items-center gap-3.5 font-mono text-xs">
              <IdCopy id={org.id} />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-9">
          <div className="text-right">
            <div className="text-text font-mono text-[28px] font-medium leading-none tracking-tight">
              {projectCount === undefined ? (
                <span className="text-text-mute">··</span>
              ) : (
                projectCount.toLocaleString()
              )}
            </div>
            <div className="text-micro text-text-mute mt-2">Projects</div>
          </div>
          <div className="text-right">
            <div className="text-text font-mono text-[28px] font-medium leading-none tracking-tight">
              {(org.assetCount ?? 0).toLocaleString()}
            </div>
            <div className="text-micro text-text-mute mt-2">Assets</div>
          </div>
          <div className="text-right">
            <div className="text-text font-mono text-[28px] font-medium leading-none tracking-tight">
              {storageUsed.num}{' '}
              <em className="text-violet text-[18px] not-italic">
                {storageUsed.unit}
              </em>
            </div>
            <div className="text-micro text-text-mute mt-2">Storage</div>
          </div>
        </div>
      </div>
    </section>
  )
}

const renderHero = (
  org: Organisation,
  projectsQuery: UseQueryResult<Project[], Error>,
) => (
  <OrgHero
    org={org}
    projectCount={
      projectsQuery.isLoading ? undefined : (projectsQuery.data?.length ?? 0)
    }
  />
)

/**
 * Which org tab a pathname selects. Exported for test (#2577 review): the
 * frozen-org takeover keys its billing exemption on this, so a change here
 * decides whether a frozen admin returning from Stripe Checkout reaches the
 * page that resumes their charge — or is bounced back into the takeover with
 * the whole suite still green. The `billing` arm in particular matches by
 * strict equality, so a future `/billing/*` child route or a trailing slash
 * would silently fall through to `'projects'`.
 */
export const getTabValueFromPath = (pathname: string, orgId: string) => {
  if (pathname === `/orgs/${orgId}`) return 'projects'
  if (pathname.startsWith(`/orgs/${orgId}/groups`)) return 'groups'
  if (pathname === `/orgs/${orgId}/members`) return 'members'
  if (pathname === `/orgs/${orgId}/billing`) return 'billing'
  if (pathname === `/orgs/${orgId}/settings`) return 'settings'
  if (pathname === `/orgs/${orgId}/permissions`) return 'permissions'
  return 'projects'
}

/**
 * Which of the org layout's renders a given org + tab selects (#2654).
 *
 * Extracted and exported for the same reason {@link getTabValueFromPath} is:
 * the component around it cannot be rendered in a unit test, so the matrix
 * would otherwise be reachable only by reading it.
 */
export type OrgLayoutView =
  'workspace' | 'pending-deletion' | 'frozen-takeover' | 'frozen-billing'

export const orgLayoutView = (
  org: Pick<Organisation, 'lifecycleState' | 'creditPlanTier'>,
  tabValue: string,
): OrgLayoutView => {
  // Deleted, inside the restore window (#2973): every tab, billing included —
  // the subscription ended at deletion, so restore is the only remedy.
  if (org.lifecycleState === 'pending_delete') return 'pending-deletion'
  if (org.lifecycleState !== 'frozen') return 'workspace'
  // An expired credit Trial keeps its workspace under a banner (#3004). Its
  // billing tab still takes the frozen arm below, for the notice above the
  // plan picker.
  if (org.creditPlanTier === 'trial' && tabValue !== 'billing') {
    return 'workspace'
  }
  return tabValue === 'billing' ? 'frozen-billing' : 'frozen-takeover'
}

const renderTabs = (
  tabValue: string,
  orgId: string,
  projectCount: number | undefined,
) => {
  return (
    <Tabs value={tabValue}>
      <TabsList className="mb-6">
        <TabsTrigger asChild value="projects" className="group">
          <Link to="/orgs/$orgId" params={{ orgId }}>
            Projects{' '}
            <TabsCount>
              {projectCount === undefined ? '··' : projectCount}
            </TabsCount>
          </Link>
        </TabsTrigger>

        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={orgId}
        >
          <TabsTrigger asChild value="members" className="group">
            <Link to="/orgs/$orgId/members" params={{ orgId }}>
              Members
            </Link>
          </TabsTrigger>
        </PermissionGate>
        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={orgId}
        >
          <TabsTrigger asChild value="groups" className="group">
            <Link to="/orgs/$orgId/groups" params={{ orgId }}>
              Groups
            </Link>
          </TabsTrigger>
        </PermissionGate>
        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={orgId}
        >
          <TabsTrigger asChild value="permissions" className="group">
            <Link to="/orgs/$orgId/permissions" params={{ orgId }}>
              Permissions
            </Link>
          </TabsTrigger>
        </PermissionGate>
        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={orgId}
        >
          <TabsTrigger asChild value="billing" className="group">
            <Link to="/orgs/$orgId/billing" params={{ orgId }}>
              Billing
            </Link>
          </TabsTrigger>
        </PermissionGate>
        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={orgId}
        >
          <TabsTrigger asChild value="settings" className="group">
            <Link to="/orgs/$orgId/settings" params={{ orgId }}>
              Settings
            </Link>
          </TabsTrigger>
        </PermissionGate>
      </TabsList>

      <TabsContent value="projects">
        <Outlet />
      </TabsContent>
      <TabsContent value="members">
        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={orgId}
        >
          <Outlet />
        </PermissionGate>
      </TabsContent>
      <TabsContent value="groups">
        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={orgId}
        >
          <Outlet />
        </PermissionGate>
      </TabsContent>
      <TabsContent value="permissions">
        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={orgId}
        >
          <Outlet />
        </PermissionGate>
      </TabsContent>
      <TabsContent value="billing">
        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={orgId}
        >
          <Outlet />
        </PermissionGate>
      </TabsContent>
      <TabsContent value="settings">
        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={orgId}
        >
          <Outlet />
        </PermissionGate>
      </TabsContent>
    </Tabs>
  )
}

function OrgDashboardLayout() {
  const { org, metadata } = Route.useLoaderData()
  const location = useLocation()
  const tabValue = getTabValueFromPath(location.pathname, org.id)
  const { projectClient } = useClient()

  // This hook will automatically set the permission context based on route params
  usePermissionCheck()

  // Which render this org + tab selects. Pure and exported so the matrix is
  // testable — see `orgLayoutView`.
  const view = orgLayoutView(org, tabValue)

  const projectsQuery = useQuery({
    queryKey: ['projects', org.id],
    queryFn: () => fetchProjects(org.id, projectClient, metadata),
    // Skip the project fetch for a frozen or deleted org — the workspace is
    // replaced by the frozen CTA / restore screen (#2046, #2973).
    enabled: view === 'workspace',
  })

  if (!org) {
    return <Spinner size="page" />
  }

  // Trial expired: replace the workspace with the choose-a-plan CTA. The org
  // GET is allow-listed by the backend gate, so the loader still resolves the
  // org (name / plan / frozenAt) needed to render this (#2046).
  //
  // The billing tab is deliberately exempt (#2574). The backend gate already
  // allow-lists every `/billing` route on the stated grounds that "the remedy
  // must be reachable" (`frozen-org-gate.ts`), and this takeover was walling it
  // anyway — so the one page that can lift the freeze was the one page a frozen
  // org could not open. That is not merely inconvenient: Lago's Checkout
  // `success_redirect_url` is org-agnostic and always returns to
  // `/orgs/$orgId/billing?checkout=success`, so an admin who added a card came
  // back to this takeover and the plan change never resumed.
  if (view === 'pending-deletion') {
    return <OrgPendingDeletionScreen org={org} metadata={metadata} />
  }

  if (view === 'frozen-takeover') {
    return <OrgFrozenScreen org={org} />
  }

  // Frozen, on billing: render the remedy with the freeze context above it, and
  // without the project hero/tabs — the projects query is disabled while frozen
  // (the read is gated backend-side too), so the hero would report a confident
  // "0 projects" to someone whose projects are intact and merely paused.
  if (view !== 'workspace') {
    return (
      <div>
        <OrgFrozenBillingNotice org={org} />
        <PermissionGate
          role={Role.ORG_ADMIN}
          resourceType="organization"
          resourceId={org.id}
          // A non-admin who reaches this URL directly (typed, bookmarked —
          // the tab itself is admin-gated, so there is no link) would
          // otherwise get the notice above an empty gate. Fall back to the
          // takeover, which tells them the one thing they can act on: ask an
          // admin (#2577 review).
          // The takeover's "access is paused" is false for a credit Trial, and
          // its notice above already says what is true.
          fallback={
            org.creditPlanTier === 'trial' ? null : (
              <OrgFrozenScreen org={org} />
            )
          }
        >
          <Outlet />
        </PermissionGate>
      </div>
    )
  }

  return (
    <div>
      {/* Off the billing tab, whose plan card is the CTA there. Leaving for
          billing unmounts the banner, which its CTA's spinner relies on. */}
      {tabValue !== 'billing' && isExpiredCreditTrial(org) && (
        <OrgTrialEndedBanner org={org} />
      )}
      {renderHero(org, projectsQuery)}
      {renderTabs(
        tabValue,
        org.id,
        projectsQuery.isLoading ? undefined : (projectsQuery.data?.length ?? 0),
      )}
    </div>
  )
}
